import React from 'react';
import { useTrackEvent } from '@olxeu-a2b/delivery-utils/src/tracking';
import { useI18n } from '../../i18n';
import {
  TrackTermsAndConditionClick,
  InfoModalContentTextKeys,
  InfoModalDescriptionTextKeys,
  InfoModalAsteriskTextKeys,
} from './types';
import ModalContent from './InfoModalContent';
import ModalDescription from './InfoModalDescription';
import ModalAsterisk, { TrackingExtras } from './InfoModalAsterisk';
import { EXTERNAL_LINKS } from '../../pages/orders/constants';

import S from './style/InfoModal';
import trackingKeys from '../constants/trackingKeys';
import InfoModalLowerDescription from './InfoModalLowerDescription';

interface Trackers {
  trackCloseModalClick: () => void;
  trackTermsAndConditionClick: TrackTermsAndConditionClick;
}

interface InfoModalProps {
  handleModalToggle: (isModalOpened: boolean) => void;
  isDesktop: boolean;
  headerTextKey: string;
  descriptionTextKeys: InfoModalDescriptionTextKeys;
  contentTextKeys: InfoModalContentTextKeys;
  lowerDescriptionTextKeys?: InfoModalDescriptionTextKeys;
  asteriskLink?: string;
  asteriskTextKeys?: InfoModalAsteriskTextKeys;
  trackers?: Trackers;
  source?: string;
  additionalDescriptionTextKey?: string;
  readMore?: string;
  readMoreLink?: string;
  trackingExtras?: TrackingExtras;
  closeModalTrackingKey?: string;
}

const { termsAndConditionsClick } = trackingKeys.events.safeDeal;

const InfoModal: React.FC<InfoModalProps> = ({
  handleModalToggle,
  isDesktop,
  trackers,
  headerTextKey,
  descriptionTextKeys,
  lowerDescriptionTextKeys,
  contentTextKeys,
  asteriskTextKeys,
  asteriskLink,
  source,
  additionalDescriptionTextKey,
  readMore,
  readMoreLink,
  trackingExtras,
  closeModalTrackingKey,
}) => {
  const i18n = useI18n();
  const trackEvent = useTrackEvent();

  const closeModal = () => {
    handleModalToggle(false);
    trackEvent(closeModalTrackingKey, null, null, trackingExtras);
    if (trackers?.trackCloseModalClick) {
      trackers.trackCloseModalClick();
    }
  };

  const onTermsAndConditionsClick = () =>
    trackEvent(termsAndConditionsClick, null, null, trackingExtras);

  return isDesktop ? (
    <S.InfoModal
      position="center"
      data-testid="info-modal-desktop"
      data-cy="info-modal-desktop"
      showCloseIcon
      onClose={closeModal}
      maskClosable
      closableOnEscPress
    >
      <S.ModalContent>
        <S.ModalImage />
        <S.ModalDesktopHeadline>{i18n(headerTextKey)}</S.ModalDesktopHeadline>
        <S.ModalBody data-testid="info-modal-body">
          <ModalDescription descriptionTextKeys={descriptionTextKeys} />
          <ModalContent contentTextKeys={contentTextKeys} />
          {lowerDescriptionTextKeys && (
            <InfoModalLowerDescription lowerDescriptionTextKeys={lowerDescriptionTextKeys} />
          )}
          {readMore && (
            <S.HelpCenterLink>
              <a
                onClick={onTermsAndConditionsClick}
                href={readMoreLink ?? EXTERNAL_LINKS.READ_MORE_ABOUT_RETURNS}
                target="_blank"
                data-testid="info-modal-read-more"
                rel="noreferrer"
              >
                {i18n(readMore)}
              </a>
            </S.HelpCenterLink>
          )}
          {additionalDescriptionTextKey && (
            <S.AdditionalDescription data-testid="info-modal-additional-desc">
              {i18n(additionalDescriptionTextKey)}
            </S.AdditionalDescription>
          )}
          {asteriskTextKeys && (
            <ModalAsterisk
              asteriskTextKeys={asteriskTextKeys}
              asteriskLink={asteriskLink}
              tracking={trackers?.trackTermsAndConditionClick}
              source={source}
              trackingExtras={trackingExtras}
            />
          )}
        </S.ModalBody>
      </S.ModalContent>
    </S.InfoModal>
  ) : (
    <S.SheetBottomModal
      closeButtonText={i18n`dlv_safedeal_info_close`}
      isVisible
      onClose={closeModal}
      data-testid="info-modal-mobile"
      data-cy="info-modal-mobile"
    >
      <S.ModalContent>
        <S.ModalImage />
        <S.ModalMobileHeadline>{i18n(headerTextKey)}</S.ModalMobileHeadline>
        <S.ModalBody data-testid="info-modal-body">
          <ModalDescription descriptionTextKeys={descriptionTextKeys} />
          <ModalContent contentTextKeys={contentTextKeys} />
          {lowerDescriptionTextKeys && (
            <InfoModalLowerDescription lowerDescriptionTextKeys={lowerDescriptionTextKeys} />
          )}
          {readMore && (
            <S.HelpCenterLink>
              <a
                onClick={onTermsAndConditionsClick}
                href={EXTERNAL_LINKS.READ_MORE_ABOUT_RETURNS}
                target="_blank"
                data-testid="info-modal-read-more"
                rel="noreferrer"
              >
                {i18n(readMore)}
              </a>
            </S.HelpCenterLink>
          )}
          {additionalDescriptionTextKey && (
            <S.AdditionalDescription data-testid="info-modal-additional-desc">
              {i18n(additionalDescriptionTextKey)}
            </S.AdditionalDescription>
          )}
          {asteriskTextKeys && (
            <ModalAsterisk
              asteriskTextKeys={asteriskTextKeys}
              asteriskLink={asteriskLink}
              tracking={trackers?.trackTermsAndConditionClick}
              source={source}
              trackingExtras={trackingExtras}
            />
          )}
        </S.ModalBody>
      </S.ModalContent>
    </S.SheetBottomModal>
  );
};

export default React.memo(InfoModal);
